import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Icons, Avatar } from '../shared/ui';

// ── nav definitions ────────────────────────────────────────────────────────────
//
// Labels and item names come from i18n (`common.json`). Building the groups
// inside the component lets us depend on the active language without rebuilding
// the entire sidebar on every render — `useTranslation()` triggers a re-render
// when the language changes, which is the only time we need new labels.

function buildGroups(t, { isAdmin, isManager }) {
  const OVERVIEW = {
    label: t('common:section.overview'),
    items: [
      { name: t('common:nav.dashboard'), href: '/manager/dashboard', I: Icons.Home },
      { name: t('common:nav.calendar'),  href: '/manager/calendar',  I: Icons.Calendar, badge: 'calendar' },
    ],
  };

  const FINANCE = {
    label: t('common:section.finance'),
    items: [
      { name: t('common:nav.finance'),  href: '/manager/finance',             I: Icons.Chart },
      { name: t('common:nav.payments'), href: '/manager/payment-management',  I: Icons.Receipt,  badge: 'payments' },
      { name: t('common:nav.meters'),   href: '/manager/meter-readings',      I: Icons.Zap,      badge: 'meters' },
    ],
  };

  const MANAGEMENT_BASE = [
    { name: t('common:nav.properties'),         href: '/manager/properties',          I: Icons.Building },
    { name: t('common:nav.propertyManagement'), href: '/manager/property-management', I: Icons.Cube },
    { name: t('common:nav.contracts'),          href: '/manager/contracts',           I: Icons.FileCheck, badge: 'contracts' },
    { name: t('common:nav.renters'),            href: '/manager/renters',             I: Icons.Users },
    { name: t('common:nav.vehicles'),           href: '/manager/vehicles',            I: Icons.Car },
    { name: t('common:nav.tickets'),            href: '/manager/tickets',             I: Icons.Ticket,    badge: 'tickets' },
    { name: t('common:nav.violations'),         href: '/manager/violations',          I: Icons.Alert },
  ];

  const MONITORING = {
    label: t('common:section.monitoring'),
    items: [
      { name: t('common:nav.cvMonitoring'), href: '/manager/cv-monitoring', I: Icons.Camera },
      { name: t('common:nav.cvSetup'),      href: '/manager/cv-setup',      I: Icons.Cog },
    ],
  };

  if (isAdmin) {
    return [
      OVERVIEW,
      FINANCE,
      {
        label: t('common:section.management'),
        items: [
          ...MANAGEMENT_BASE.slice(0, 3),
          { name: t('common:nav.staff'), href: '/manager/users', I: Icons.Shield },
          ...MANAGEMENT_BASE.slice(3),
        ],
      },
      MONITORING,
    ];
  }

  if (isManager) {
    return [
      OVERVIEW,
      FINANCE,
      { label: t('common:section.management'), items: MANAGEMENT_BASE },
      MONITORING,
    ];
  }

  // Renter — uses the manager portal as a stripped-down dashboard.
  return [
    {
      label: t('common:section.overview'),
      items: [
        { name: t('common:nav.home'),        href: '/manager/dashboard',   I: Icons.Home },
        { name: t('common:nav.myPayments'),  href: '/manager/my-payments', I: Icons.Receipt },
        { name: t('common:nav.myMeters'),    href: '/manager/my-meters',   I: Icons.Zap },
        { name: t('common:nav.tickets'),     href: '/manager/tickets',     I: Icons.Ticket },
        { name: t('common:nav.myContract'),  href: '/manager/my-contract', I: Icons.FileCheck },
      ],
    },
  ];
}

// ── sub-components ─────────────────────────────────────────────────────────────

function NavBadge({ count, active }) {
  if (!count) return null;
  return (
    <span
      className={`ml-auto text-[11px] font-semibold num min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center flex-shrink-0 ${
        active ? 'bg-teal-600 text-white' : 'bg-stone-100 text-stone-600'
      }`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function OrgSwitcher() {
  return (
    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors">
      <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-bold text-white">A</span>
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold text-stone-900 truncate leading-tight">Sunrise Apt.</p>
        <p className="text-[11px] text-stone-400 truncate">Toa nhà · Quận 7</p>
      </div>
      <Icons.ChevronDown size={14} className="text-stone-400 flex-shrink-0" />
    </button>
  );
}

function SearchBar({ onOpen }) {
  const { t } = useTranslation('common');
  return (
    <div className="px-3 pb-2">
      <button
        onClick={onOpen}
        className="w-full flex items-center gap-2 px-3 h-9 rounded-lg bg-stone-50 border border-stone-200 hover:border-stone-300 transition-colors text-left"
      >
        <Icons.Search size={14} className="text-stone-400 flex-shrink-0" />
        <span className="flex-1 text-sm text-stone-400">{t('nav.search')}</span>
        <kbd className="text-[10px] text-stone-400 bg-white border border-stone-200 rounded px-1.5 py-0.5 font-medium">⌘K</kbd>
      </button>
    </div>
  );
}

function NavSection({ group, badges, onClose }) {
  return (
    <div className="mb-1">
      <p className="px-4 py-2 text-[10px] font-semibold tracking-[0.1em] text-stone-400 uppercase select-none">
        {group.label}
      </p>
      <div className="space-y-0.5 px-2">
        {group.items.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/manager/dashboard'}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 h-9 rounded-lg text-[13px] font-medium transition-colors ${
                isActive
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.I
                  size={16}
                  className={`flex-shrink-0 ${isActive ? 'text-teal-600' : 'text-stone-400'}`}
                />
                <span className="flex-1 truncate">{item.name}</span>
                {item.badge && (
                  <NavBadge count={badges[item.badge]} active={isActive} />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

// ── sidebar body ───────────────────────────────────────────────────────────────

function SidebarBody({ groups, user, badges, onClose, isMobile }) {
  const { t } = useTranslation('common');
  const fullName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.username || t('user.guest');
  const roleLabel =
    user?.role === 'admin' ? t('role.admin') :
    user?.role === 'manager' ? t('role.manager') : t('role.renter');

  return (
    <div className="flex flex-col h-full bg-white border-r border-stone-200">
      {/* Org switcher */}
      <div className="flex items-center justify-between border-b border-stone-100 flex-shrink-0">
        <OrgSwitcher />
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 mr-2 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
          >
            <Icons.X size={18} />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="pt-3 pb-1 flex-shrink-0">
        <SearchBar />
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-1">
        {groups.map((group) => (
          <NavSection key={group.label} group={group} badges={badges} onClose={onClose} />
        ))}
      </nav>

      {/* User card */}
      <div className="p-3 border-t border-stone-100 flex-shrink-0">
        <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer group">
          <div className="relative flex-shrink-0">
            <Avatar name={fullName} size={32} />
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-stone-900 truncate leading-tight">{fullName}</p>
            <p className="text-[11px] text-stone-400 truncate">{roleLabel} · {t('user.online')}</p>
          </div>
          <button className="p-1 rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100 opacity-0 group-hover:opacity-100 transition-all">
            <Icons.More size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── exported component ────────────────────────────────────────────────────────

export default function AppSidebar({ isOpen, onClose, badges = {} }) {
  const { t } = useTranslation('common');
  const { user, isAdmin, isManager } = useAuth();
  const groups = buildGroups(t, { isAdmin, isManager });

  return (
    <>
      {/* Mobile */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-[232px] transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarBody groups={groups} user={user} badges={badges} onClose={onClose} isMobile />
      </div>
      {/* Desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:w-[232px] lg:flex lg:flex-col">
        <SidebarBody groups={groups} user={user} badges={badges} onClose={() => {}} />
      </div>
    </>
  );
}
